import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { Sort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { GridEmitter, GridOptions, UiGridComponent } from '@program-suite/ui-grid';
import { LookupStore } from '@program-suite/data-common';
import { SnackbarComponent, ToastType } from '@program-suite/ui';
import { InspectionsStore } from '../data/inspections.store';
import { InspectionsService } from '../data/inspections.service';
import { STATUS_LABELS } from '../data/inspection-status-labels';

// Every inspection is automated through Coverage Validator until manual inspections exist.
const TYPE = 'Automated';
const COMPANY = 'Coverage Validator';

// Only a completed inspection has a report to download.
const DISABLE_UNTIL_COMPLETE = { column: 'completedDate', values: [null, undefined] };

@Component({
  selector: 'lib-inspections-grid',
  imports: [UiGridComponent],
  templateUrl: './inspections-grid.component.html',
  styleUrl: './inspections-grid.component.scss',
})
export class InspectionsGridComponent implements OnInit {
  inspectionsStore = inject(InspectionsStore);
  inspectionsService = inject(InspectionsService);
  router = inject(Router);
  snackbar = inject(MatSnackBar);
  lookupStore = inject(LookupStore);
  defaultSortColumn = 'createDate';
  defaultSortDirection = 'DESCENDING';
  data = computed(() =>
    (this.inspectionsStore.resource.value()?.rows ?? []).map(row => ({
      ...row,
      type: TYPE,
      company: COMPANY,
      status: row.status ? STATUS_LABELS[row.status] : '',
      completed: row.completedDate ? formatDate(row.completedDate, 'MM/dd/yyyy', 'en-US') : 'Incomplete',
    })),
  );
  totalNumberOfItems = computed<number>(() => this.inspectionsStore.resource.value()?.totalCount ?? 0);
  statuses = Object.entries(STATUS_LABELS).map(([id, name]) => ({ id, name }));
  types = [{ id: TYPE, name: TYPE }];

  gridOptions = signal<GridOptions>({
    filterData: [
      { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
      { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 0 },
      { name: 'Search', formControlName: 'searchTerm', type: 'search' },
      { name: 'Type', formControlName: 'type', type: 'dropdown', lookup: this.types },
      { name: 'Status', formControlName: 'status', type: 'dropdown', lookup: this.statuses },
      { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    ],
    filterValues: this.inspectionsStore.filterValues,
    openFilters: true,
    columns: [
      { header: 'Inspection #', controlName: 'inspectionNumber' },
      { header: 'Policy #', controlName: 'policyNumber' },
      { header: 'Business Name', controlName: 'businessName' },
      { header: 'Type', controlName: 'type', hideColumnSort: true },
      { header: 'Company', controlName: 'company', hideColumnSort: true },
      { header: 'Ordered', controlName: 'createDate', displayPipe: 'date' },
      { header: 'Status', controlName: 'status' },
      { header: 'Date', controlName: 'lastNoticeDate', displayPipe: 'date' },
      { header: 'Completed', controlName: 'completed' },
      {
        header: 'Manage',
        controlName: 'action',
        hideColumnSort: true,
        actionColumnitems: [
          {
            type: 'icon',
            buttonAction: 'viewReport',
            conditionalDisplay: { calculateDisplay: () => 'eye', column: 'completedDate' },
          },
          {
            type: 'icon',
            buttonAction: 'downloadReport',
            conditionalDisplay: { calculateDisplay: () => 'download', column: 'completedDate' },
            disableOnColumnValue: DISABLE_UNTIL_COMPLETE,
          },
        ],
      },
    ],
    data: this.data,
    totalNumberOfItems: this.totalNumberOfItems,
    frontEndPagination: false,
    fetchData: false,
    displaySearch: false,
    useUtcDates: true,
  });

  ngOnInit(): void {
    this.lookupStore.loadLookup('programs');
    // reload grid everytime this page is hit
    this.inspectionsStore.resource.reload();
  }

  fetchAll(rawParams: any) {
    // type has a single value, so it filters nothing and isn't sent.
    const { sortOrder, type, ...filters } = rawParams ?? {};
    const sort = this.sortField(sortOrder);
    this.inspectionsStore.setFilterValues({
      ...filters,
      SortField: sort.active,
      SortDirection: sort.direction ?? this.defaultSortDirection,
    });
  }

  sortField(sort: Sort | null | undefined) {
    if (!sort?.active || !sort.direction) {
      return { active: this.defaultSortColumn, direction: this.defaultSortDirection };
    }
    const active = sort.active === 'completed' ? 'completedDate' : sort.active;
    return { active, direction: sort.direction === 'asc' ? 'ASCENDING' : 'DESCENDING' };
  }

  async actionEvent(event: GridEmitter) {
    const row = event.data as any;
    if (event.action === 'fetchAll' || event.action === 'formChanges' || event.action === 'clearFilters') {
      this.fetchAll(event.data);
    } else if (event.action === 'viewReport') {
      this.router.navigate(['/inspections', row.inspectionGuid]);
    } else if (event.action === 'downloadReport') {
      try {
        await this.inspectionsService.downloadInspectionReport(row.inspectionGuid);
      } catch (error: any) {
        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: { toastType: ToastType.Error, message: error.message },
        });
      }
    }
  }
}
